import { clearAppStorage } from 'src/common/storage';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Avatar,
  Box,
  Button,
  Divider,
  Hidden,
  lighten,
  ListItem,
  ListItemText,
  Popover,
  Typography
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ExpandMoreTwoToneIcon from '@mui/icons-material/ExpandMoreTwoTone';
import LockOpenTwoToneIcon from '@mui/icons-material/LockOpenTwoTone';
import { setGlobalState, useGlobalState } from 'src/state/globalstate';
import styles from '../header.module.scss';
import InfoIcon from '@mui/icons-material/Info';
import ListSubheader from '@mui/material/ListSubheader';
import List from '@mui/material/List';
import DatasetIcon from '@mui/icons-material/Dataset';
import DescriptionIcon from '@mui/icons-material/Description';
import { eDiscoveryUrl } from 'src/common/routes';
import Axios, { API } from 'src/common/api';
import { captureLogout } from 'src/common/analytics';
import AppsIcon from '@mui/icons-material/Apps';
import BugReportIcon from '@mui/icons-material/BugReport';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';

const UserBoxButton = styled(Button)(
  ({ theme }) => `
        display: flex;
        align-items: center;
        gap: ${theme.spacing(0.75)};
        padding: ${theme.spacing(0.6)} ${theme.spacing(1)};
        border-radius: 100px;
        text-transform: none;
        transition: background .18s ease;
        &:hover {
          background: rgba(108,92,231,0.07);
        }
`
);

const MenuUserBox = styled(Box)(
  ({ theme }) => `
        background: ${theme.colors.alpha.black[5]};
        padding: ${theme.spacing(2)};
`
);

const UserBoxText = styled(Box)(
  ({ theme }) => `
        text-align: left;
`
);

const UserBoxLabel = styled(Typography)(
  ({ theme }) => `
        font-weight: ${theme.typography.fontWeightBold};
        color: ${theme.palette.secondary.main};
        display: block;
`
);

const UserBoxDescription = styled(Typography)(
  ({ theme }) => `
        color: ${lighten(theme.palette.secondary.main, 0.5)}
`
);

const user = {
  email: localStorage.getItem('email'),
  avatar: '/static/images/avatars/6.jpg',
  jobtitle: 'admin'
};

function HeaderUserbox() {
  let navigate = useNavigate();

  const [languageArray] = useGlobalState('languageArray');
  const [userRolesList] = useGlobalState('userRoles');
  const [subMenu, setsubMenu] = useState(false);
  const [showLogs, setshowLogs] = useState(false);

  const Logoutuser = () => {
    captureLogout('manual');
    const rememberCheck = localStorage.getItem('rememberCheck');
    const rememberuser = localStorage.getItem('rememberuser');
    const selectedLanguage = localStorage.getItem('language');
    const languageType = localStorage.getItem('languageType');
    clearAppStorage();
    localStorage.setItem('language', selectedLanguage);
    localStorage.setItem('languageType', languageType);
    handleClose();
    navigate('/login');
    setGlobalState('loggedin', false);
    localStorage.setItem('rememberCheck', rememberCheck);
    localStorage.setItem('rememberuser', rememberuser);
  };

  const OpenRules = () => {
    navigate(eDiscoveryUrl.Rules);
    setOpen(false);
    setsubMenu(false);
  };

  const handleClick = () => {
    if (subMenu) {
      setsubMenu(false);
    } else if (!subMenu) {
      setsubMenu(true);
    }
  };

  // `email` can be absent for a brief window on a hard refresh, or if the
  // session was cleared out from under us — guard so a null never crashes the
  // whole app tree via the error boundary ("Something went wrong").
  const email = localStorage.getItem('email') || '';
  const name = email ? email.split('@') : [''];

  const user = {
    email: email,
    name: name[0],
    avatar: '/static/images/avatars/7.png',
    jobtitle: 'admin'
  };

  // Capitalize the first letter for display, e.g. "shaheryar" -> "Shaheryar"
  const displayName = user.name
    ? user.name.charAt(0).toUpperCase() + user.name.slice(1)
    : user.name;

  // Build initials from the name, e.g. "shaheryar" -> "SH", "john.doe" -> "JD"
  const getInitials = (raw: string): string => {
    if (!raw) return 'U';
    const parts = raw.split(/[.\s_-]+/).filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return raw.slice(0, 2).toUpperCase();
  };
  const userInitials = getInitials(user.name);

  // The id->role-name map is loaded by the Sidebar / User Management page.
  // Fetch it here too so the header shows the correct role standalone.
  useEffect(() => {
    if (userRolesList && userRolesList.length > 0) return;
    (async () => {
      try {
        const res = await Axios.get(API.userRoles);
        setGlobalState('userRoles', res.data);
      } catch (e) {
        // ignore — falls back to default label until roles load
      }
    })();
  }, []);

  // Resolve the logged-in user's role NAME from the id captured at login
  // (localStorage 'userRole'), mapping it via the userRoles API data. Works
  // whether localStorage holds an id ("2") or already a name ("Admin").
  const rawUserRole = localStorage.getItem('userRole') || '';
  const roleName = String(rawUserRole)
    .split(',')
    .map((part) => {
      const key = part.trim();
      if (!key) return '';
      const found = (userRolesList || []).find(
        (r: any) => String(r.id) === key
      );
      if (found) return String(found.roles);
      // A bare numeric id we couldn't map yet = not fetched -> show nothing.
      return /^\d+$/.test(key) ? '' : key;
    })
    .filter(Boolean)
    .join(', ')
    .trim();
  // Only render the role chip once we have a real, resolved role name.
  const hasRole = roleName.length > 0;
  const roleLabel = roleName;

  // Same color scheme as the User Management "Role" column badges.
  // `light` = on light surfaces (closed header), `dark` = on the popover's
  // dark gradient header. Categories mirror roleClass() in UserManagement.
  const getRoleChip = (name: string) => {
    const n = (name || '').toLowerCase();
    if (n.includes('super'))
      return {
        dot: '#ca8a04',
        light: { bg: 'rgba(202,138,4,.14)', color: '#a16207', border: 'rgba(202,138,4,.3)' },
        dark: { bg: 'rgba(202,138,4,.18)', color: '#e0b64a', border: 'rgba(202,138,4,.32)' }
      };
    if (n.includes('admin'))
      return {
        dot: '#6c5ce7',
        light: { bg: 'rgba(108,92,231,.12)', color: '#5a48d0', border: 'rgba(108,92,231,.22)' },
        dark: { bg: 'rgba(139,123,240,.16)', color: '#b9aef5', border: 'rgba(139,123,240,.3)' }
      };
    if (n.includes('review'))
      return {
        dot: '#2fb079',
        light: { bg: 'rgba(47,176,121,.13)', color: '#1d7a45', border: 'rgba(47,176,121,.24)' },
        dark: { bg: 'rgba(47,176,121,.16)', color: '#5fd99a', border: 'rgba(47,176,121,.3)' }
      };
    if (n.includes('approv'))
      return {
        dot: '#f59e0b',
        light: { bg: 'rgba(245,158,11,.15)', color: '#b7791f', border: 'rgba(245,158,11,.28)' },
        dark: { bg: 'rgba(245,158,11,.16)', color: '#f0b75a', border: 'rgba(245,158,11,.32)' }
      };
    if (n.includes('editor') || n.includes('content'))
      return {
        dot: '#0ea5b8',
        light: { bg: 'rgba(14,165,184,.13)', color: '#0e7c8b', border: 'rgba(14,165,184,.26)' },
        dark: { bg: 'rgba(14,165,184,.16)', color: '#4fd0e0', border: 'rgba(14,165,184,.3)' }
      };
    return {
      dot: '#6366f1',
      light: { bg: 'rgba(99,102,241,.11)', color: '#4f46e5', border: 'rgba(99,102,241,.22)' },
      dark: { bg: 'rgba(139,123,240,.16)', color: '#b9aef5', border: 'rgba(139,123,240,.3)' }
    };
  };
  const roleChip = getRoleChip(roleLabel);

  const ref = useRef<any>(null);
  const [isOpen, setOpen] = useState<boolean>(false);

  const handleOpen = (): void => {
    setsubMenu(false);
    setOpen(true);
  };

  const handleClose = (): void => {
    setOpen(false);
  };

  const LoginLogs = () => {
    console.log('Login Logs');
    handleClose();
    navigate(eDiscoveryUrl.LoginLogs);
  };

  const appFeatures = () => {
    console.log('appFeatures');
    handleClose();
    navigate(eDiscoveryUrl.appFeatures);
  };

  const changeLogs = () => {
    console.log('changeLogs');
    handleClose();
    navigate(eDiscoveryUrl.changeLogs);
  };

  const UserManagementLogs = () => {
    console.log('UserManagementLogs');
    handleClose();
    navigate(eDiscoveryUrl.UserManagement);
  };

  useEffect(() => {
    const getAdminEmails = async () => {
      try {
        await Axios.get(API.LogAccessEmails).then((res) => {
          let adminEmails = res.data;
          console.log('adminEmails ===');
          console.log(adminEmails);

          let filteredEmails = adminEmails.filter((e) => e === email);
          console.log(filteredEmails);
          if (filteredEmails.length > 0) {
            setshowLogs(true);
            setGlobalState('cloudStorageVisibility', true);
          } else if (filteredEmails.length == 0) {
            setshowLogs(false);
            setGlobalState('cloudStorageVisibility', false);
          }
          //

          return Promise.resolve();
        });
      } catch (err) {
        // return Promise.reject();
      }
    };
    getAdminEmails();
  }, []);

  return (
    <>
      <UserBoxButton color="secondary" ref={ref} onClick={handleOpen}>
        <Avatar
          alt={user.name}
          sx={{
            width: 38,
            height: 38,
            background: 'linear-gradient(135deg,#ab9bed 0%,#6c5ce7 100%)',
            color: '#fff',
            fontFamily: "'Inter', sans-serif",
            fontWeight: 700,
            fontSize: '14px',
            letterSpacing: '0.5px',
            boxShadow: '0 4px 12px -3px rgba(108,92,231,0.5)'
          }}
        >
          {userInitials}
        </Avatar>
        <Hidden mdDown>
          <UserBoxText>
            <UserBoxLabel
              variant="body1"
              className={styles.username}
              sx={{
                fontSize: '14px',
                fontWeight: 600,
                lineHeight: 1.2,
                letterSpacing: '.1px'
              }}
            >
              {displayName}
            </UserBoxLabel>
          </UserBoxText>
        </Hidden>
        <Hidden smDown>
          <ExpandMoreTwoToneIcon sx={{ ml: 0.25 }} />
        </Hidden>
      </UserBoxButton>
      <Popover
        anchorEl={ref.current}
        onClose={handleClose}
        open={isOpen}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        disableScrollLock={true}
        PaperProps={{
          sx: {
            mt: 1.5,
            minWidth: 296,
            borderRadius: '18px',
            overflow: 'hidden',
            border: '1px solid rgba(21,18,58,0.08)',
            boxShadow: '0 28px 70px -20px rgba(40,25,90,0.42)',
            fontFamily: "'Inter', sans-serif"
          }
        }}
      >
        {/* User header */}
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            p: 2.25,
            background:
              'linear-gradient(135deg,#15123a 0%,#3a2f7a 70%,#6c5ce7 135%)'
          }}
        >
          <Box sx={{ position: 'relative', flex: 'none' }}>
            <Avatar
              sx={{
                width: 50,
                height: 50,
                border: '2.5px solid rgba(255,255,255,0.4)',
                background: 'linear-gradient(135deg,#ab9bed 0%,#6c5ce7 100%)',
                color: '#fff',
                fontFamily: "'Inter', sans-serif",
                fontWeight: 700,
                fontSize: '18px',
                letterSpacing: '0.5px'
              }}
            >
              {userInitials}
            </Avatar>
            <Box
              sx={{
                position: 'absolute',
                right: 0,
                bottom: 1,
                width: 13,
                height: 13,
                borderRadius: '50%',
                background: '#3ddc84',
                border: '2.5px solid #2a1f5e'
              }}
            />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                color: '#fff',
                fontFamily: "'Inter', sans-serif",
                fontWeight: 700,
                fontSize: '15.5px',
                lineHeight: 1.2,
                textTransform: 'capitalize',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              {displayName}
            </Typography>
            <Typography
              sx={{
                color: 'rgba(255,255,255,0.72)',
                fontSize: '12px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              {user.email}
            </Typography>
            {hasRole && (
              <Box
                component="span"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.6,
                  mt: 0.85,
                  px: 0.9,
                  py: 0.3,
                  borderRadius: '100px',
                  textTransform: 'capitalize',
                  background: roleChip.dark.bg,
                  border: `1px solid ${roleChip.dark.border}`,
                  color: roleChip.dark.color,
                  fontSize: '10px',
                  fontWeight: 700,
                  letterSpacing: '0.3px'
                }}
              >
                {roleLabel}
              </Box>
            )}
          </Box>
        </Box>

        <Divider sx={{ borderColor: 'rgba(21,18,58,0.07)' }} />

        <Box sx={{ p: 1 }}>
          <ListItem
            button={true}
            onClick={Logoutuser}
            sx={{
              borderRadius: '12px',
              color: '#ef4444',
              py: 1.1,
              '&:hover': { background: 'rgba(239,68,68,0.08)' }
            }}
          >
            <LockOpenTwoToneIcon
              fontSize="small"
              sx={{ mr: 1.25, color: '#ef4444' }}
            />
            <ListItemText
              primary={
                languageArray.length > 0 && languageArray
                  ? languageArray[91]
                  : 'Log out'
              }
              primaryTypographyProps={{ fontSize: '14px', fontWeight: 600 }}
            />
            <KeyboardArrowRightRoundedIcon
              sx={{ fontSize: 18, color: '#ef4444', opacity: 0.55 }}
            />
          </ListItem>
        </Box>
      </Popover>
    </>
  );
}

export default HeaderUserbox;
